import { EmailAddressZ } from "@sellar-io/common";
import {
  useCurrentDate,
  Button,
  InputContainer,
  InputDate,
  InputEmail,
  InputLabel,
  InputOption,
  InputPhoneNumber,
  InputPostalCode,
  InputSelect,
  InputText,
} from "@sellar-io/components";
import {
  CountryTypeZ,
  NationalityCodeZ,
  nationalityCodeDictionary,
} from "@sellar-io/common";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { FormField } from "../onboarding/components/FormField";
import { CountrySelectField } from "../onboarding/components/CountrySelectField";

const GenderSchema = z
  .enum(["", "Male", "Female"])
  .refine((gender) => gender !== "", "Gender is required");

const RequiredCountrySchema = CountryTypeZ.or(z.literal(""))
  .refine((country) => country !== "", "Country is required")
  .pipe(CountryTypeZ);

const RyftDirectorFormSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required"),
  middleName: z.string().trim().optional(),
  lastName: z.string().trim().min(1, "Last name is required"),
  email: EmailAddressZ,
  dateOfBirth: z.string().min(1, "Date of birth is required"),
  countryOfBirth: RequiredCountrySchema,
  gender: GenderSchema,
  nationalities: z
    .array(NationalityCodeZ)
    .min(1, "Select at least one nationality")
    .refine(
      (nationalities) => new Set(nationalities).size === nationalities.length,
      "Each nationality can only be selected once",
    ),
  address: z.object({
    lineOne: z.string().trim().min(1, "Address line is required"),
    city: z.string().trim().min(1, "City is required"),
    country: RequiredCountrySchema,
    postalCode: z.string().trim().min(1, "Postal code is required"),
  }),
  phoneNumber: z.string().trim().min(1, "Phone number is required"),
});

export type RyftDirectorFormValues = z.input<typeof RyftDirectorFormSchema>;
export type RyftDirectorSubmissionValues = z.output<
  typeof RyftDirectorFormSchema
>;

const genders = [
  { label: "Male", value: "Male" },
  { label: "Female", value: "Female" },
] satisfies ReadonlyArray<{
  label: string;
  value: z.infer<typeof GenderSchema>;
}>;

export const RyftEditDirectorForm = ({
  mode,
  initialData,
  isSubmitting,
  disabled = false,
  onSubmit,
}: {
  mode: "create" | "update";
  initialData: RyftDirectorFormValues;
  isSubmitting: boolean;
  disabled?: boolean;
  onSubmit: (values: RyftDirectorSubmissionValues) => void | Promise<void>;
}) => {
  const currentDate = useCurrentDate();
  const form = useForm({
    defaultValues: initialData,
    validators: {
      onChange: RyftDirectorFormSchema,
    },
    onSubmit: ({ value }) => {
      if (disabled || isSubmitting) return;
      return onSubmit(RyftDirectorFormSchema.parse(value));
    },
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
      }}
    >
      <fieldset
        className="flex flex-col gap-4 disabled:cursor-not-allowed disabled:opacity-60"
        disabled={isSubmitting || disabled}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <form.Field name="firstName">
            {({ state, handleBlur, handleChange }) => (
              <FormField
                label="First name"
                errorMessage={state.meta.errors[0]?.message}
              >
                <InputText
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(event.target.value)}
                  autoComplete="given-name"
                  required
                />
              </FormField>
            )}
          </form.Field>
          <form.Field name="middleName">
            {({ state, handleBlur, handleChange }) => (
              <FormField
                label="Middle name"
                errorMessage={state.meta.errors[0]?.message}
              >
                <InputText
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(event.target.value)}
                  autoComplete="additional-name"
                />
              </FormField>
            )}
          </form.Field>
        </div>

        <form.Field name="lastName">
          {({ state, handleBlur, handleChange }) => (
            <FormField
              label="Last name"
              errorMessage={state.meta.errors[0]?.message}
            >
              <InputText
                value={state.value}
                onBlur={handleBlur}
                onChange={(event) => handleChange(event.target.value)}
                autoComplete="family-name"
                required
              />
            </FormField>
          )}
        </form.Field>

        <form.Field name="email">
          {({ state, handleBlur, handleChange }) => (
            <FormField
              label="Email address"
              errorMessage={state.meta.errors[0]?.message}
            >
              <InputEmail
                value={state.value}
                autoComplete="email"
                onBlur={handleBlur}
                onChange={(event) => handleChange(event.target.value)}
                readOnly={mode === "create"}
                required
                disabled={mode === "create"}
              />
            </FormField>
          )}
        </form.Field>

        <div className="grid gap-4 md:grid-cols-2">
          <form.Field name="dateOfBirth">
            {({ state, handleBlur, handleChange }) => (
              <FormField
                label="Date of birth"
                errorMessage={state.meta.errors[0]?.message}
              >
                <InputDate
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(event.target.value)}
                  max={currentDate.toISOString().slice(0, 10)}
                  required
                />
              </FormField>
            )}
          </form.Field>
          <form.Field name="countryOfBirth">
            {({ state, handleBlur, handleChange }) => (
              <CountrySelectField
                label="Country of birth"
                value={state.value}
                errorMessage={state.meta.errors[0]?.message}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            )}
          </form.Field>
        </div>

        <form.Field name="gender">
          {({ state, handleBlur, handleChange }) => (
            <FormField
              label="Gender"
              errorMessage={state.meta.errors[0]?.message}
            >
              <InputSelect
                value={state.value}
                onBlur={handleBlur}
                onChange={(event) =>
                  handleChange(GenderSchema.parse(event.target.value))
                }
                required
              >
                <InputOption value="" disabled>
                  Select a gender
                </InputOption>
                {genders.map((gender) => (
                  <InputOption key={gender.value} value={gender.value}>
                    {gender.label}
                  </InputOption>
                ))}
              </InputSelect>
            </FormField>
          )}
        </form.Field>

        <form.Field name="nationalities">
          {({ state, handleBlur, handleChange }) => {
            const addNationality = () => {
              const nextNationality = NationalityCodeZ.options.find(
                (countryCode) => !state.value.includes(countryCode),
              );

              if (nextNationality) {
                handleChange([...state.value, nextNationality]);
              }
            };

            return (
              <div className="flex flex-col gap-1">
                <InputLabel>Nationalities</InputLabel>
                <div className="flex flex-col gap-2">
                  {state.value.map((nationality, index) => (
                    <div
                      className="flex items-center gap-2"
                      key={`${nationality}-${index}`}
                    >
                      <InputContainer className="flex-1">
                        <InputSelect
                          value={nationality}
                          onBlur={handleBlur}
                          onChange={(event) => {
                            const nextNationalities = [...state.value];
                            nextNationalities[index] = NationalityCodeZ.parse(
                              event.target.value,
                            );
                            handleChange(nextNationalities);
                          }}
                          required
                        >
                          {Object.entries(nationalityCodeDictionary).map(
                            ([countryCode, nationality]) => (
                              <InputOption
                                key={countryCode}
                                value={countryCode}
                              >
                                {nationality}
                              </InputOption>
                            ),
                          )}
                        </InputSelect>
                      </InputContainer>
                      {state.value.length > 1 && (
                        <Button
                          type="button"
                          color="inverse-red"
                          size="small"
                          onClick={() =>
                            handleChange(
                              state.value.filter(
                                (_, nationalityIndex) =>
                                  nationalityIndex !== index,
                              ),
                            )
                          }
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    color="tertiary"
                    size="small"
                    disabled={
                      state.value.length === NationalityCodeZ.options.length
                    }
                    onClick={addNationality}
                  >
                    Add another nationality
                  </Button>
                </div>
                {state.meta.errors[0]?.message && (
                  <span role="alert" className="text-sm text-red-700">
                    {state.meta.errors[0].message}
                  </span>
                )}
              </div>
            );
          }}
        </form.Field>

        <div className="border-t border-zinc-200 pt-2">
          <h3 className="font-semibold text-zinc-900">Address</h3>
          <p className="mt-1 text-sm text-zinc-600">
            Enter the director&apos;s current address.
          </p>
        </div>

        <form.Field name="address.lineOne">
          {({ state, handleBlur, handleChange }) => (
            <FormField
              label="Address line"
              errorMessage={state.meta.errors[0]?.message}
            >
              <InputText
                value={state.value}
                onBlur={handleBlur}
                onChange={(event) => handleChange(event.target.value)}
                autoComplete="address-line1"
                required
              />
            </FormField>
          )}
        </form.Field>

        <div className="grid gap-4 md:grid-cols-2">
          <form.Field name="address.city">
            {({ state, handleBlur, handleChange }) => (
              <FormField
                label="City"
                errorMessage={state.meta.errors[0]?.message}
              >
                <InputText
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(event.target.value)}
                  autoComplete="address-level2"
                  required
                />
              </FormField>
            )}
          </form.Field>
          <form.Field name="address.country">
            {({ state, handleBlur, handleChange }) => (
              <CountrySelectField
                label="Country"
                value={state.value}
                errorMessage={state.meta.errors[0]?.message}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            )}
          </form.Field>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <form.Subscribe selector={(state) => state.values.address.country}>
            {(country) => (
              <form.Field name="address.postalCode">
                {({ state, handleBlur, handleChange }) => (
                  <FormField
                    label="Postal code"
                    errorMessage={state.meta.errors[0]?.message}
                  >
                    <InputPostalCode
                      value={state.value}
                      onBlur={handleBlur}
                      onChange={(event) => handleChange(event.target.value)}
                      country={country || undefined}
                      required
                    />
                  </FormField>
                )}
              </form.Field>
            )}
          </form.Subscribe>
          <form.Field name="phoneNumber">
            {({ state, handleBlur, handleChange }) => (
              <FormField
                label="Phone number"
                errorMessage={state.meta.errors[0]?.message}
                containerClassName="overflow-visible"
              >
                <InputPhoneNumber
                  value={state.value}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  inputProps={{
                    autoComplete: "tel",
                    name: "phoneNumber",
                    required: true,
                  }}
                />
              </FormField>
            )}
          </form.Field>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={isSubmitting || disabled}
            loading={isSubmitting}
          >
            {mode === "create" ? "Add director" : "Save changes"}
          </Button>
        </div>
      </fieldset>
    </form>
  );
};
